import React, { useEffect, useMemo, useRef, useState } from 'react';

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

// view: 'days' | 'months' | 'years'
function CustomDateInput({
  value,
  onChange,
  placeholder = 'Select date',
  disabled = false,
}) {
  const isMobile = useIsMobile();
  const wrapperRef = useRef(null);
  const initialDate = parseDate(value) || new Date();

  const [open, setOpen] = useState(false);
  const [view, setView] = useState('days'); // 'days' | 'months' | 'years'
  const [viewDate, setViewDate] = useState(
    new Date(initialDate.getFullYear(), initialDate.getMonth(), 1)
  );

  useEffect(() => {
    const parsed = parseDate(value);
    if (parsed) {
      setViewDate(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setView('days');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const days = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const firstWeekDay = (firstDayOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells = [];
    for (let i = 0; i < firstWeekDay; i += 1) {
      cells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(new Date(year, month, day));
    }
    return cells;
  }, [viewDate]);

  // Build a range of years: current year ± 10
  const years = useMemo(() => {
    const y = viewDate.getFullYear();
    const start = y - 10;
    const end = y + 10;
    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [viewDate]);

  const selectedDate = parseDate(value);

  const handleSelectDate = (date) => {
    if (!date) return;
    const formatted = formatDateForValue(date);
    if (typeof onChange === 'function') {
      onChange(formatted);
    }
    setOpen(false);
    setView('days');
  };

  const handlePrevMonth = () => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handlePrevYear = () => {
    setViewDate((prev) => new Date(prev.getFullYear() - 1, prev.getMonth(), 1));
  };

  const handleNextYear = () => {
    setViewDate((prev) => new Date(prev.getFullYear() + 1, prev.getMonth(), 1));
  };

  const handleSelectMonth = (monthIndex) => {
    setViewDate(new Date(viewDate.getFullYear(), monthIndex, 1));
    setView('days');
  };

  const handleSelectYear = (year) => {
    setViewDate(new Date(year, viewDate.getMonth(), 1));
    setView('months');
  };

  const headerTitle = view === 'days'
    ? viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : view === 'months'
    ? String(viewDate.getFullYear())
    : `${years[0]} – ${years[years.length - 1]}`;

  return (
    <div ref={wrapperRef} style={wrapperStyle}>
      <button
        type="button"
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => !prev);
            setView('days');
          }
        }}
        style={{
          ...triggerStyle,
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        disabled={disabled}
      >
        <span style={triggerTextStyle}>
          {selectedDate ? formatDateForDisplay(selectedDate) : placeholder}
        </span>
        <span style={calendarIconStyle}>📅</span>
      </button>

      {open && (
        <div style={isMobile ? mobilePopupStyle : popupStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <button
              type="button"
              onClick={view === 'days' ? handlePrevMonth : handlePrevYear}
              style={navButtonStyle}
            >
              ←
            </button>

            <button
              type="button"
              onClick={() => {
                if (view === 'days') setView('months');
                else if (view === 'months') setView('years');
                else setView('days');
              }}
              style={monthTitleButtonStyle}
              title={view === 'days' ? 'Click to pick month/year' : view === 'months' ? 'Click to pick year' : 'Back to day view'}
            >
              {headerTitle}
              <span style={titleChevronStyle}>▾</span>
            </button>

            <button
              type="button"
              onClick={view === 'days' ? handleNextMonth : handleNextYear}
              style={navButtonStyle}
            >
              →
            </button>
          </div>

          {/* Day view */}
          {view === 'days' && (
            <>
              <div style={weekdaysStyle}>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                  <div key={day} style={weekdayStyle}>
                    {day}
                  </div>
                ))}
              </div>

              <div style={daysGridStyle}>
                {days.map((date, index) => {
                  if (!date) {
                    return <div key={'empty-' + index} />;
                  }

                  const isSelected =
                    selectedDate &&
                    date.getFullYear() === selectedDate.getFullYear() &&
                    date.getMonth() === selectedDate.getMonth() &&
                    date.getDate() === selectedDate.getDate();

                  const isToday = isSameDay(date, new Date());

                  return (
                    <button
                      key={formatDateForValue(date)}
                      type="button"
                      onClick={() => handleSelectDate(date)}
                      style={{
                        ...dayButtonStyle,
                        ...(isSelected ? selectedDayButtonStyle : null),
                        ...(isToday && !isSelected ? todayDayButtonStyle : null),
                      }}
                    >
                      {date.getDate()}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Month view */}
          {view === 'months' && (
            <div style={monthGridStyle}>
              {MONTHS.map((name, i) => {
                const isCurrent =
                  selectedDate &&
                  selectedDate.getFullYear() === viewDate.getFullYear() &&
                  selectedDate.getMonth() === i;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => handleSelectMonth(i)}
                    style={{
                      ...monthCellStyle,
                      ...(isCurrent ? selectedMonthCellStyle : null),
                    }}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          )}

          {/* Year view */}
          {view === 'years' && (
            <div style={yearGridStyle}>
              {years.map((y) => {
                const isCurrent =
                  selectedDate && selectedDate.getFullYear() === y;
                const isView = viewDate.getFullYear() === y;
                return (
                  <button
                    key={y}
                    type="button"
                    onClick={() => handleSelectYear(y)}
                    style={{
                      ...yearCellStyle,
                      ...(isCurrent ? selectedYearCellStyle : null),
                      ...(isView && !isCurrent ? currentViewYearStyle : null),
                    }}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div style={footerStyle}>
            <button
              type="button"
              onClick={() => {
                if (typeof onChange === 'function') {
                  onChange('');
                }
                setOpen(false);
                setView('days');
              }}
              style={secondaryButtonStyle}
            >
              Clear
            </button>

            <button
              type="button"
              onClick={() => handleSelectDate(new Date())}
              style={primaryButtonStyle}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseDate(value) {
  if (!value) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);

  const date = new Date(year, month, day);
  if (Number.isNaN(date.getTime())) return null;

  return date;
}

function formatDateForValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateForDisplay(date) {
  return date.toLocaleDateString('en-CA');
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const wrapperStyle = {
  position: 'relative',
  width: '100%',
};

const triggerStyle = {
  width: '100%',
  minHeight: '48px',
  borderRadius: '12px',
  border: '1px solid var(--border)',
  background: 'var(--input-bg)',
  color: 'var(--text-primary)',
  padding: '12px 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  textAlign: 'left',
  fontSize: '14px',
};

const triggerTextStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const calendarIconStyle = {
  flexShrink: 0,
};

const popupStyle = {
  position: 'absolute',
  top: 'calc(100% + 6px)',
  left: 0,
  background: 'var(--popup-bg)',
  border: '1px solid var(--popup-border)',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  zIndex: 200,
  width: '320px',
  padding: '14px',
};

const mobilePopupStyle = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  background: 'var(--popup-bg)',
  border: '1px solid var(--popup-border)',
  borderRadius: '16px',
  boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
  zIndex: 1000,
  width: '320px',
  maxWidth: 'calc(100vw - 32px)',
  padding: '14px',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
  gap: '6px',
};

const monthTitleButtonStyle = {
  flex: 1,
  background: 'transparent',
  border: '1px solid var(--popup-border)',
  borderRadius: '10px',
  color: 'var(--popup-text)',
  fontWeight: 800,
  fontSize: '14px',
  cursor: 'pointer',
  padding: '6px 10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
};

const titleChevronStyle = {
  fontSize: '11px',
  opacity: 0.6,
};

const navButtonStyle = {
  background: 'transparent',
  border: '1px solid var(--popup-border)',
  borderRadius: '10px',
  color: 'var(--popup-text)',
  width: '36px',
  height: '36px',
  cursor: 'pointer',
  flexShrink: 0,
};

const weekdaysStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '6px',
  marginBottom: '8px',
};

const weekdayStyle = {
  textAlign: 'center',
  fontSize: '12px',
  color: 'var(--popup-text)',
  fontWeight: 700,
  padding: '4px 0',
  opacity: 0.7,
};

const daysGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: '6px',
};

const dayButtonStyle = {
  height: '38px',
  borderRadius: '10px',
  border: '1px solid var(--popup-item-border)',
  background: 'var(--popup-item-bg)',
  color: 'var(--popup-text)',
  cursor: 'pointer',
  fontWeight: 600,
};

const selectedDayButtonStyle = {
  background: 'rgba(255,255,255,0.95)',
  border: '1px solid rgba(255,255,255,0.95)',
  color: 'var(--primary)',
  fontWeight: 800,
};

const todayDayButtonStyle = {
  border: '2px solid rgba(255,255,255,0.8)',
};

const monthGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '8px',
  padding: '4px 0',
};

const monthCellStyle = {
  padding: '12px 8px',
  borderRadius: '10px',
  border: '1px solid var(--popup-item-border)',
  background: 'var(--popup-item-bg)',
  color: 'var(--popup-text)',
  fontWeight: 700,
  fontSize: '14px',
  cursor: 'pointer',
};

const selectedMonthCellStyle = {
  background: 'rgba(255,255,255,0.95)',
  border: '1px solid rgba(255,255,255,0.95)',
  color: 'var(--primary)',
};

const yearGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '6px',
  maxHeight: '200px',
  overflowY: 'auto',
  padding: '4px 0',
};

const yearCellStyle = {
  padding: '10px 4px',
  borderRadius: '10px',
  border: '1px solid var(--popup-item-border)',
  background: 'var(--popup-item-bg)',
  color: 'var(--popup-text)',
  fontWeight: 600,
  fontSize: '13px',
  cursor: 'pointer',
};

const selectedYearCellStyle = {
  background: 'rgba(255,255,255,0.95)',
  border: '1px solid rgba(255,255,255,0.95)',
  color: 'var(--primary)',
};

const currentViewYearStyle = {
  border: '2px solid rgba(255,255,255,0.6)',
};

const footerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '10px',
  marginTop: '14px',
};

const secondaryButtonStyle = {
  flex: 1,
  minHeight: '40px',
  borderRadius: '10px',
  border: '1px solid var(--popup-border)',
  background: 'var(--popup-item-bg)',
  color: 'var(--popup-text)',
  cursor: 'pointer',
  fontWeight: 700,
};

const primaryButtonStyle = {
  flex: 1,
  minHeight: '40px',
  borderRadius: '10px',
  border: 'none',
  background: 'var(--primary)',
  color: '#ffffff',
  cursor: 'pointer',
  fontWeight: 800,
};

export default CustomDateInput;
